import { useState } from 'react'
import type { FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { Button } from '@/components/Button'
import { TextField } from '@/components/Form'
import { OutageBanner } from '@/components/OutageBanner'
import { FullPageLoader } from '@/components/RequireAuth'
import { useAuth } from '@/hooks/useAuth'
import { useStatus } from '@/hooks/useStatus'
import { ApiError } from '@/lib/api'

export function LoginPage() {
  const { isAuthenticated, isLoading, login, isLoggingIn } = useAuth()
  // Same cached `/api/status` read the banner uses: no extra request, but it
  // lets this page say plainly why sign-in is unavailable.
  const { services, down } = useStatus()
  const navigate = useNavigate()
  const location = useLocation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  const from = (location.state as { from?: string } | null)?.from ?? '/'

  // Jellyfin is the identity provider, so if it is down nobody can sign in —
  // that is worth saying before someone types a password and gets refused.
  const identityDown = down.some((service) => service.service.toLowerCase() === 'jellyfin')

  if (isLoading) return <FullPageLoader label="Checking your session" />
  if (isAuthenticated) return <Navigate to={from} replace />

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    try {
      await login({ username: username.trim(), password })
      navigate(from, { replace: true })
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.code === 'upstream_unavailable' || cause.code === 'upstream_error'
            ? 'The media server did not answer, so sign-in could not be checked. Try again in a few minutes.'
            : cause.message
          : 'Could not sign in. Try again.',
      )
    }
  }

  return (
    <div className="min-h-dvh">
      <OutageBanner />
      <div className="mx-auto flex min-h-[calc(100dvh-3rem)] max-w-sm flex-col justify-center px-4 py-10">
        <header className="mb-6">
          <p className="signage text-lg text-ink">Hermes</p>
          <div aria-hidden className="rule-signal mt-3 h-px w-full" />
          <p className="mt-3 text-sm text-muted">
            Sign in with the username and password you use for the media server.
          </p>
        </header>

        {/* The board's defining condition: no feed. Stated before the form, not
            after a failed attempt. */}
        {identityDown ? (
          <p
            role="status"
            className="mb-4 rounded-[3px] border border-warn/30 bg-warn-dim px-3 py-2.5 text-sm text-ink"
          >
            <span className="signage mb-1 block text-[9px] text-warn">No feed</span>
            The media server is unreachable, and sign-in is checked against it. Everything else here
            keeps working — try again once it is back.
          </p>
        ) : null}

        <form onSubmit={onSubmit} className="space-y-4 rounded border border-line bg-surface p-4">
          <TextField
            label="Username"
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            required
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
          <TextField
            label="Password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />

          {error ? (
            <p
              role="alert"
              className="rounded-[3px] border border-danger/40 bg-danger-dim px-3 py-2 text-sm text-ink"
            >
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            loading={isLoggingIn}
            disabled={!username.trim() || !password}
          >
            Sign in
          </Button>
        </form>

        <p className="mt-5 text-center text-xs text-muted">
          Need an account? Ask whoever runs the server for an invite link.
        </p>
        {services.length > 0 ? (
          <p className="mt-4 flex items-center justify-center gap-1.5 text-center">
            <span
              aria-hidden
              className={`size-1.5 rounded-full ${down.length === 0 ? 'bg-ok' : 'bg-warn'}`}
            />
            <span className="font-mono text-[10px] text-faint">
              {services.length - down.length}/{services.length}
            </span>
            <span className="signage text-[9px] text-faint">services up</span>
          </p>
        ) : null}
      </div>
    </div>
  )
}
